import { blinkConstants } from '@thoughtspot/blink-constants';
import _ from 'lodash';
import { useMemo } from 'react';
import {
    ApolloError,
    NetworkStatus,
    useMutation,
    WatchQueryFetchPolicy,
} from '../../utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';
import {
    CreateRelationshipDocument,
    CreateRelationshipMutation,
    CreateRelationshipMutationVariables,
    ListMetadataHeaderParams,
    ListType,
    Metadata,
    MetadataListParamsInput,
    SaveDocument,
    SaveMutation,
    SaveMutationVariables,
    SourceDetail,
    Tag,
    useGetEmbraceSourcesLazyQuery as _useGetEmbraceSourcesLazyQuery,
    useGetPopularColumnsQuery as _useGetPopularColumnsQuery,
    useGetRelationshipDetailsQuery as _useGetRelationshipDetailsQuery,
    useGetSourceDetailLazyQuery as _useGetSourceDetailLazyQuery,
    useGetSourceDetailQuery as _useGetSourceDetailQuery,
    useGetSourcesListByDataSourceLazyQuery as _useGetSourcesListByDataSourceLazyQuery,
    useGetSourcesQuery as _useGetSourcesQuery,
    useGetTablesListQuery as _useGetTablesListQuery,
    useGetTagDetailsQuery as _useGetTagDetailsQuery,
    useGetV2SourceDetailLazyQuery as _useGetV2SourceDetailLazyQuery,
    useGetV2SourceDetailQuery as _useGetV2SourceDetailQuery,
    useGetV2SourcesQuery as _useGetV2SourcesQuery,
    useGetV2SourcesWithPaginationQuery as _useGetV2SourcesWithPaginationQuery,
} from '../generated/graphql-types';
import { excludeCohortColumns, sortSourceDetails } from './data-source.util';

const getSources = (data: any, dontSort: boolean) => {
    const sortByObjName = (objA: any, objB: any) => {
        const nameA = objA.name.toLowerCase();
        const nameB = objB.name.toLowerCase();
        return nameA > nameB ? 1 : -1;
    };

    const getSortedArray = <T extends any[]>(arr: T, _dontSort = false) => {
        if (arr?.length) {
            if (dontSort) return arr;
            return [...arr].sort(sortByObjName);
        }
        return [];
    };

    const sources = {
        imported: getSortedArray(data?.getSourcesList?.imported),
        tables: getSortedArray(data?.getSourcesList?.tables),
        views: getSortedArray(data?.getSourcesList?.views),
        worksheets: getSortedArray(data?.getSourcesList?.worksheets),
        sqlViews: getSortedArray(data?.getSourcesList?.sqlViews),
    };

    const allSources = [
        ...getSortedArray(data?.getSourcesList?.worksheets, true),
        ...getSortedArray(data?.getSourcesList?.views, true),
        ...getSortedArray(data?.getSourcesList?.sqlViews, true),
        ...getSortedArray(data?.getSourcesList?.imported, true),
        ...getSortedArray(data?.getSourcesList?.tables, true),
    ];

    const tags = getSortedArray(data?.getSourcesList?.tags);

    return {
        sources,
        tags,
        allSources,
    };
};

const getSourcesMemoized = _.memoize(getSources, data => {
    return [
        'key',
        data?.getSourcesList?.worksheets?.length,
        data?.getSourcesList?.views?.length,
        data?.getSourcesList?.sqlViews?.length,
        data?.getSourcesList?.imported?.length,
        data?.getSourcesList?.tables?.length,
    ].join('-');
});

export const useGetSourcesQuery = (
    shouldRefetchData = false,
    dontSort = false,
) => {
    const { data, error, loading } = _useGetSourcesQuery({
        fetchPolicy: shouldRefetchData ? 'network-only' : 'cache-first',
    });
    return {
        error,
        loading,
        ...getSources(data, dontSort),
    };
};

export const useGetFalconSourcesLazyQuery = () => {
    const [
        getSourcesListByDataSource,
        { data, error, loading },
    ] = _useGetSourcesListByDataSourceLazyQuery({
        fetchPolicy: 'network-only',
    });
    const dataSourceGuid = blinkConstants.DEFAULT_DATASOURCE_ID;
    const response = data?.getSourcesListByDataSource;
    const sources = [
        ...(response?.tables || []),
        ...(response?.views || []),
        ...(response?.imported || []),
        ...(response?.worksheets || []),
        ...(response?.sqlViews || []),
    ];
    return {
        getFalconSourcesFn: () => {
            return getSourcesListByDataSource({
                variables: { dataSourceGuid },
            });
        },
        sources,
        error,
        loading,
    };
};

export const useGetEmbraceSourcesLazyQuery = () => {
    const [
        getEmbraceSourcesFn,
        { loading, data, error },
    ] = _useGetEmbraceSourcesLazyQuery({
        fetchPolicy: 'network-only',
    });

    const response = data?.getEmbraceSourcesList || {};
    const embraceSources = [
        ...(response?.tables || []),
        ...(response?.views || []),
        ...(response?.imported || []),
        ...(response?.worksheets || []),
        ...(response?.sqlViews || []),
    ];

    return {
        getEmbraceSourcesFn,
        error,
        loading,
        embraceSources,
    };
};

export const useGetSourcesListByDataSourceLazyQuery = () => {
    const [
        getSourcesListByDataSource,
        { data, error, loading },
    ] = _useGetSourcesListByDataSourceLazyQuery({
        fetchPolicy: 'network-only',
    });
    const sources = data?.getSourcesListByDataSource || {
        tables: [],
        worksheets: [],
        views: [],
        imported: [],
        sqlViews: [],
    };

    return {
        getSourcesListByDataSource: (dataSourceGuid: string) => {
            return getSourcesListByDataSource({
                variables: { dataSourceGuid },
            });
        },
        sources,
        error,
        loading,
    };
};

export const useGetSourceDetailQuery = (
    ids: string[],
    fetchPolicy: WatchQueryFetchPolicy = 'cache-and-network',
) => {
    const { data, error, loading } = _useGetSourceDetailQuery({
        fetchPolicy,
        variables: {
            ids,
        },
        skip: ids === undefined,
    });

    const sourceDetails: SourceDetail[] = useMemo(() => {
        let newDetails = data?.getSourceDetailById;
        if (!newDetails?.length) return undefined;

        newDetails = excludeCohortColumns(newDetails);
        newDetails = sortSourceDetails(newDetails) as SourceDetail[];
        return newDetails;
    }, [data?.getSourceDetailById]);

    return {
        error,
        loading,
        data: sourceDetails,
    };
};

export const useGetTableColumnsLazyQuery = () => {
    const [
        getSourceDetailFn,
        { loading, data, error },
    ] = _useGetSourceDetailLazyQuery({
        fetchPolicy: 'network-only',
    });

    return {
        getSourceDetailFn,
        error,
        loading,
        data: data?.getSourceDetailById,
    };
};

export const useGetTagDetailsQuery = (ids: string[]) => {
    const { data, error, loading } = _useGetTagDetailsQuery({
        variables: {
            ids,
        },
        skip: ids === undefined,
    });

    return {
        error,
        loading,
        data: data?.getSourceDetailById,
    };
};

export const useGetRelationshipDetailsQuery = (ids: string[]) => {
    const { data, error, loading } = _useGetRelationshipDetailsQuery({
        fetchPolicy: 'network-only',
        variables: {
            ids,
        },
    });
    return {
        error,
        loading,
        data: data?.getRelationshipDetailsById,
    };
};

export const useGetTablesList = (params: ListMetadataHeaderParams) => {
    const { data, error, loading } = _useGetTablesListQuery({
        fetchPolicy: 'network-only',
        variables: {
            params,
        },
    });

    return {
        error,
        loading,
        data: data?.getTablesList,
    };
};

export const useSaveMutation = () => {
    const [save, { data, error, loading }] = useMutation<
        SaveMutation,
        SaveMutationVariables
    >(SaveDocument);

    return {
        data,
        error,
        loading,
        save: (type: ListType, content: string, forcesave: boolean) =>
            save({
                variables: {
                    type,
                    content,
                    forcesave,
                },
            }),
    };
};

export const useCreateRelationshipMutation = () => {
    const [createRelationship, { data, error, loading }] = useMutation<
        CreateRelationshipMutation,
        CreateRelationshipMutationVariables
    >(CreateRelationshipDocument);

    return {
        data,
        error,
        loading,
        createRelationship: (
            name: string,
            sourceTableId: string,
            destTableId: string,
            sourceColumnIds: string[],
            destColumnIds: string[],
            joinType: string,
            isOneToOneJoin: boolean,
            description: string,
            saveRelationship: boolean,
        ) =>
            createRelationship({
                variables: {
                    name,
                    sourceTableId,
                    destTableId,
                    sourceColumnIds,
                    destColumnIds,
                    joinType,
                    isOneToOneJoin,
                    description,
                    saveRelationship,
                },
            }),
    };
};

export const useGetPopularColumnsQuery = (ids: string[]) => {
    const { data, error, loading } = _useGetPopularColumnsQuery({
        variables: {
            ids,
        },
    });
    return {
        data: data?.getPopularColumns,
        error,
        loading,
    };
};

export interface UseGetSourcesV2QueryReturnType {
    error: ApolloError | undefined;
    loading: boolean;
    sources: {
        imported: Metadata[];
        tables: Metadata[];
        views: Metadata[];
        worksheets: Metadata[];
        sqlViews: Metadata[];
    };
    tags: Tag[];
    allSources: Metadata[];
    catalogConfig: {
        desc_show: boolean;
        tc_show: boolean;
    } | null;
}

export const useGetSourcesV2Query = (
    shouldRefetchData = false,
    dontSort = false,
    useMemoizedSources = false,
    skipCall = false,
    noUncaughtException = false,
): UseGetSourcesV2QueryReturnType => {
    const { data, error, loading } = _useGetV2SourcesQuery({
        fetchPolicy: shouldRefetchData ? 'network-only' : 'cache-first',
        skip: skipCall,
        ...(noUncaughtException
            ? {
                  errorPolicy: 'ignore',
              }
            : {}),
    });

    const catalogConfig = {
        desc_show: data?.getSourcesListForAnswer?.desc_show,
        tc_show: data?.getSourcesListForAnswer?.tc_show,
    };

    const sources = useMemoizedSources
        ? getSourcesMemoized(
              {
                  getSourcesList: data?.getSourcesListForAnswer,
                  getTagsList: data?.getTagsList,
              },
              dontSort,
          )
        : getSources(
              {
                  getSourcesList: data?.getSourcesListForAnswer,
                  getTagsList: data?.getTagsList,
              },
              dontSort,
          );

    return {
        error,
        loading,
        ...sources,
        catalogConfig: catalogConfig ?? null,
    };
};

export const useGetV2SourcesWithPaginationQuery = (
    params: MetadataListParamsInput,
    shouldRefetchData = false,
    skipCall = false,
    noUncaughtException = false,
) => {
    const sortParams = {
        sortAscending: true,
        sort: params.pattern ? 'DEFAULT' : 'NAME',
    };

    const {
        data,
        error,
        fetchMore,
        networkStatus,
    } = _useGetV2SourcesWithPaginationQuery({
        variables: {
            params: {
                ...sortParams,
                ...params,
            },
        },
        fetchPolicy: shouldRefetchData ? 'network-only' : 'cache-first',
        skip: skipCall,
        ...(noUncaughtException
            ? {
                  errorPolicy: 'ignore',
              }
            : {}),
        notifyOnNetworkStatusChange: true,
    });

    const catalogConfig = {
        desc_show: data?.getSourcesListForAnswer?.desc_show,
        tc_show: data?.getSourcesListForAnswer?.tc_show,
    };

    const sources: Metadata[] =
        data?.getSourcesListForAnswerWithPagination?.sources || [];
    const tags = data?.getSourcesListForAnswerWithPagination?.tags || [];
    const isLastBatch =
        data?.getSourcesListForAnswerWithPagination?.isLastBatch;

    const loading = networkStatus === NetworkStatus.loading;
    const isFetchingMore = networkStatus === NetworkStatus.fetchMore;

    function fetchMoreFn(params: MetadataListParamsInput) {
        return fetchMore({
            variables: {
                params: {
                    ...sortParams,
                    ...params,
                },
            },
        });
    }

    return {
        error,
        loading,
        sources,
        tags,
        isLastBatch,
        catalogConfig: catalogConfig ?? null,
        fetchMore: fetchMoreFn,
        isFetchingMore,
    };
};

export const useSelectedSourcesMetadataQuery = (
    selectedIds: string[],
    skip = false,
) => {
    const { data, error, loading } = _useGetV2SourcesWithPaginationQuery({
        variables: {
            params: {
                fetchIds: selectedIds,
            },
        },
        skip: skip || !selectedIds || selectedIds.length === 0,
        fetchPolicy: 'cache-first',
    });

    const catalogConfig = {
        desc_show: data?.getSourcesListForAnswer?.desc_show,
        tc_show: data?.getSourcesListForAnswer?.tc_show,
    };

    const selectedSources =
        data?.getSourcesListForAnswerWithPagination?.sources;

    return {
        error,
        loading,
        selectedSources,
        catalogConfig: catalogConfig ?? null,
    };
};

export const useGetV2SourceDetailQuery = (
    ids: string[],
    shouldExcludeCohortColumns: boolean,
    fetchPolicy: WatchQueryFetchPolicy = 'cache-and-network',
) => {
    const { data, error, loading } = _useGetV2SourceDetailQuery({
        fetchPolicy,
        variables: {
            ids,
        },
        skip: ids === undefined,
    });

    const sourceDetails: SourceDetail[] = useMemo(() => {
        let newDetails = data?.getSourceDetailByIdForAnswer;
        if (!newDetails?.length) return undefined;

        if (shouldExcludeCohortColumns) {
            newDetails = excludeCohortColumns(newDetails);
        }
        newDetails = sortSourceDetails(newDetails) as SourceDetail[];
        return newDetails;
    }, [data?.getSourceDetailByIdForAnswer]);

    return {
        error,
        loading,
        data: sourceDetails,
    };
};

/**
 * Given an array of worksheet/source GUIDs, returns the unique connection
 * (dataSourceId) GUIDs for those sources.  Uses cache-first so callers that
 * share the same sources won't trigger extra network requests.
 */
export const useConnectionIdsForSources = (sourceIds: string[]): string[] => {
    const { data: sourceDetails } = useGetV2SourceDetailQuery(
        sourceIds,
        false,
        'cache-first',
    );
    return useMemo(
        () => [
            ...new Set(
                sourceDetails
                    ?.map(s => s.dataSourceId)
                    .filter((id): id is string => !!id),
            ),
        ],
        [sourceDetails],
    );
};

export const useGetV2SourceDetailLazyQuery = (
    shouldExcludeCohortColumns: boolean,
    fetchPolicy: WatchQueryFetchPolicy = 'cache-and-network',
) => {
    const [
        getV2SourcesDetails,
        { data, error, loading },
    ] = _useGetV2SourceDetailLazyQuery({
        fetchPolicy,
    });

    const sourceDetails: SourceDetail[] = useMemo(() => {
        let newDetails = data?.getSourceDetailByIdForAnswer;
        if (!newDetails?.length) return undefined;

        if (shouldExcludeCohortColumns) {
            newDetails = excludeCohortColumns(newDetails);
        }
        newDetails = sortSourceDetails(newDetails) as SourceDetail[];
        return newDetails;
    }, [data?.getSourceDetailByIdForAnswer]);

    return {
        getV2SourcesDetails: (ids: string[]) => {
            return getV2SourcesDetails({
                variables: {
                    ids,
                },
            });
        },
        error,
        loading,
        data: sourceDetails,
    };
};
